import * as React from "react";
import { useDataProvider } from "@refinedev/core";
import { SkeletonTable } from "@/components/SkeletonTable";
import { ErrorBanner } from "@/components/ErrorBanner";
import { EmptyState } from "@/components/EmptyState";

// ---------------------------------------------------------------------------
// Domain types (mirrors backend /admin/users/:id/content contract)
// ---------------------------------------------------------------------------

interface PlanItem {
  id: string;
  name: string;
  type: string;
  position: number;
}

interface PlanDay {
  dayNumber: number;
  date?: string | null;
  theme?: string | null;
  items: PlanItem[];
}

interface PlanHistoryEntry {
  seq: number;
  type: string;
  label: string;
  source: "ai" | "server" | string;
  date: string;
  day?: number | null;
  place?: string | null;
}

interface Plan {
  id: string;
  title: string;
  status: string;
  departureCityName?: string | null;
  createdAt: string;
  updatedAt: string;
  days: PlanDay[];
  history: PlanHistoryEntry[];
}

interface ContentData {
  plans: Plan[];
}

// ---------------------------------------------------------------------------
// Domain types for /admin/users/:id/chat
// ---------------------------------------------------------------------------

interface ChatMessage {
  id: string;
  role: "user" | "assistant" | string;
  content: string;
  createdAt: string;
}

interface ChatSession {
  id: string;
  tripId?: string | null;
  scope?: string | null;
  createdAt: string;
  messages: ChatMessage[];
}

interface ChatData {
  items: ChatSession[];
  nextCursor?: string | null;
}

// ---------------------------------------------------------------------------
// Domain types for /admin/users/:id/saved
// ---------------------------------------------------------------------------

interface SavedSource {
  id: string;
  type?: string | null;
  url?: string | null;
  title?: string | null;
  savedAt?: string | null;
}

interface UserPlace {
  id: string;
  name?: string | null;
  address?: string | null;
  savedAt?: string | null;
}

interface SavedData {
  savedSources: SavedSource[];
  userPlaces: UserPlace[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(d);
}

function formatDateShort(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

// ---------------------------------------------------------------------------
// Source badge for plan history entries
// ---------------------------------------------------------------------------

function PlanSourceBadge({ source }: { source: string }): React.JSX.Element {
  const isAi = source === "ai";
  const label = isAi ? "Чат" : "Вручную";
  const className = isAi
    ? "text-[#0055aa] bg-[rgba(0,113,227,0.06)] border-[rgba(0,113,227,0.15)]"
    : "text-[#7a5200] bg-[rgba(196,138,26,0.06)] border-[rgba(196,138,26,0.15)]";

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-[4px] text-[11px] font-[600] border ${className}`}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Plan history timeline item (mirrors EntitlementEventItem style)
// ---------------------------------------------------------------------------

function PlanHistoryItem({ entry }: { entry: PlanHistoryEntry }): React.JSX.Element {
  return (
    <div className="relative pl-7 pb-7 last:pb-0" role="listitem">
      <div
        className="absolute left-0 top-1 w-4 h-4 rounded-full border-2 z-[1] border-[var(--border)] bg-[var(--surface)]"
        aria-hidden="true"
      />
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] px-4 py-3.5 hover:shadow-sm transition-shadow">
        <div className="flex items-center gap-2.5 mb-1 flex-wrap">
          <span className="text-[14px] font-[600] text-[var(--fg)]">{entry.label}</span>
          <PlanSourceBadge source={entry.source} />
          <span className="ml-auto text-[12px] text-[var(--muted)] font-mono">
            {formatDate(entry.date)}
          </span>
        </div>
        {(entry.day != null || entry.place) && (
          <div className="text-[12px] text-[var(--muted)] flex gap-3 flex-wrap">
            {entry.day != null && (
              <span>
                День: <strong className="text-[var(--fg)] font-[500]">{entry.day}</strong>
              </span>
            )}
            {entry.place && (
              <span>
                Место: <strong className="text-[var(--fg)] font-[500]">{entry.place}</strong>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plans section
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }): React.JSX.Element {
  const map: Record<string, { label: string; className: string }> = {
    draft: {
      label: "Черновик",
      className: "text-[var(--muted)] bg-[var(--surface-2)] border-[var(--border)]",
    },
    published: {
      label: "Опубликован",
      className: "text-[#1a8a52] bg-[rgba(26,138,82,0.10)] border-[rgba(26,138,82,0.20)]",
    },
    archived: {
      label: "Архив",
      className: "text-[var(--muted)] bg-[var(--surface-2)] border-[var(--border)]",
    },
  };
  const entry = map[status];
  const label = entry?.label ?? status;
  const className =
    entry?.className ?? "text-[var(--muted)] bg-[var(--surface-2)] border-[var(--border)]";

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-[4px] text-[11px] font-[600] border ${className}`}
    >
      {label}
    </span>
  );
}

function PlanCard({ plan }: { plan: Plan }): React.JSX.Element {
  const [expanded, setExpanded] = React.useState(false);
  const totalItems = plan.days.reduce((acc, d) => acc + (d.items?.length ?? 0), 0);

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] overflow-hidden">
      <div className="px-4 py-3.5 flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[14px] font-[600] text-[var(--fg)] truncate">{plan.title}</span>
            <StatusBadge status={plan.status} />
          </div>
          <div className="flex gap-4 text-[12px] text-[var(--muted)] flex-wrap">
            {plan.departureCityName && <span>{plan.departureCityName}</span>}
            <span>Создан: {formatDateShort(plan.createdAt)}</span>
            <span>Изменён: {formatDateShort(plan.updatedAt)}</span>
            <span>{plan.days.length} {plan.days.length === 1 ? "день" : "дней"} · {totalItems} {totalItems === 1 ? "место" : "мест"}</span>
          </div>
        </div>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={`plan-detail-${plan.id}`}
          className="text-[12px] text-[var(--accent)] font-[500] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] rounded px-1 py-0.5 flex-shrink-0"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Свернуть" : "Раскрыть"}
        </button>
      </div>

      {expanded && (
        <div id={`plan-detail-${plan.id}`} className="border-t border-[var(--border)]">
          {/* Days & items */}
          {plan.days.length > 0 && (
            <div className="px-4 py-3 border-b border-[var(--border)]">
              <h4 className="text-[12px] font-[600] uppercase tracking-wide text-[var(--muted)] mb-2">
                Дни и места
              </h4>
              <div className="space-y-2">
                {plan.days.map((day) => (
                  <div key={day.dayNumber}>
                    <div className="text-[12px] font-[600] text-[var(--fg)] mb-1">
                      День {day.dayNumber}
                      {day.date && (
                        <span className="font-[400] text-[var(--muted)] ml-2">{formatDateShort(day.date)}</span>
                      )}
                      {day.theme && (
                        <span className="font-[400] text-[var(--muted)] ml-2">— {day.theme}</span>
                      )}
                    </div>
                    {day.items && day.items.length > 0 ? (
                      <ul className="pl-3 space-y-0.5">
                        {day.items.map((item) => (
                          <li key={item.id} className="text-[12px] text-[var(--fg)] flex gap-2">
                            <span className="text-[var(--muted)] w-5 flex-shrink-0">{item.position}.</span>
                            <span>{item.name}</span>
                            <span className="text-[var(--muted)]">({item.type})</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="pl-3 text-[12px] text-[var(--muted)]">Нет мест</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* History timeline */}
          {plan.history && plan.history.length > 0 ? (
            <div className="px-4 py-3">
              <h4 className="text-[12px] font-[600] uppercase tracking-wide text-[var(--muted)] mb-3">
                История правок
              </h4>
              <div
                className="relative pl-7 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-0.5 before:bg-[var(--border)] before:rounded-sm"
                role="list"
                aria-label="История правок плана"
              >
                {[...plan.history]
                  .sort((a, b) => b.seq - a.seq)
                  .map((entry) => (
                    <PlanHistoryItem key={entry.seq} entry={entry} />
                  ))}
              </div>
            </div>
          ) : (
            <div className="px-4 py-3">
              <p className="text-[12px] text-[var(--muted)]">История правок пуста</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PlansSection({ userId }: { userId: string }): React.JSX.Element {
  const [isLoading, setIsLoading] = React.useState(true);
  const [isError, setIsError] = React.useState(false);
  const [plans, setPlans] = React.useState<Plan[]>([]);
  const getDataProvider = useDataProvider();
  const dpRef = React.useRef(getDataProvider());

  React.useEffect(() => {
    dpRef.current = getDataProvider();
  });

  const fetchPlans = React.useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const result = await dpRef.current.custom?.({
        url: `/admin/users/${userId}/content`,
        method: "get",
      });
      const data = result?.data as ContentData | undefined;
      setPlans(data?.plans ?? []);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  React.useEffect(() => {
    void fetchPlans();
  }, [fetchPlans]);

  if (isLoading) {
    return <SkeletonTable columns={4} rows={3} />;
  }

  if (isError) {
    return (
      <ErrorBanner
        message="Ошибка загрузки планов"
        onRetry={() => { void fetchPlans(); }}
      />
    );
  }

  if (plans.length === 0) {
    return <EmptyState message="Нет планов" />;
  }

  return (
    <div className="space-y-3" aria-label="Планы пользователя">
      {plans.map((plan) => (
        <PlanCard key={plan.id} plan={plan} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chat section
// ---------------------------------------------------------------------------

function ChatSection({ userId }: { userId: string }): React.JSX.Element {
  const [isLoading, setIsLoading] = React.useState(true);
  const [isError, setIsError] = React.useState(false);
  const [sessions, setSessions] = React.useState<ChatSession[]>([]);
  const [expandedSession, setExpandedSession] = React.useState<string | null>(null);
  const getDataProvider = useDataProvider();
  const dpRef = React.useRef(getDataProvider());

  React.useEffect(() => {
    dpRef.current = getDataProvider();
  });

  const fetchChat = React.useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const result = await dpRef.current.custom?.({
        url: `/admin/users/${userId}/chat`,
        method: "get",
      });
      const data = result?.data as ChatData | undefined;
      setSessions(data?.items ?? []);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  React.useEffect(() => {
    void fetchChat();
  }, [fetchChat]);

  if (isLoading) {
    return <SkeletonTable columns={3} rows={3} />;
  }

  if (isError) {
    return (
      <ErrorBanner
        message="Ошибка загрузки чата"
        onRetry={() => { void fetchChat(); }}
      />
    );
  }

  if (sessions.length === 0) {
    return <EmptyState message="Нет истории чата" />;
  }

  return (
    <div className="space-y-3" aria-label="Сессии чата">
      {sessions.map((session) => {
        const isOpen = expandedSession === session.id;
        const msgCount = session.messages?.length ?? 0;

        return (
          <div
            key={session.id}
            className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] overflow-hidden"
          >
            <div className="px-4 py-3.5 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-[600] text-[var(--fg)] mb-0.5">
                  {session.scope ?? "Чат-сессия"}
                </div>
                <div className="flex gap-4 text-[12px] text-[var(--muted)] flex-wrap">
                  <span>{formatDate(session.createdAt)}</span>
                  <span>{msgCount} {msgCount === 1 ? "сообщение" : "сообщений"}</span>
                </div>
              </div>
              {msgCount > 0 && (
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`chat-session-${session.id}`}
                  className="text-[12px] text-[var(--accent)] font-[500] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] rounded px-1 py-0.5 flex-shrink-0"
                  onClick={() => setExpandedSession(isOpen ? null : session.id)}
                >
                  {isOpen ? "Свернуть" : "Открыть"}
                </button>
              )}
            </div>

            {isOpen && (
              <div
                id={`chat-session-${session.id}`}
                className="border-t border-[var(--border)] divide-y divide-[var(--border)]"
              >
                {(session.messages ?? []).map((msg) => (
                  <div key={msg.id} className="px-4 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`text-[11px] font-[600] uppercase tracking-wide ${
                          msg.role === "user"
                            ? "text-[var(--accent)]"
                            : "text-[var(--muted)]"
                        }`}
                      >
                        {msg.role === "user" ? "Пользователь" : "Ассистент"}
                      </span>
                      <span className="text-[11px] text-[var(--muted)] font-mono ml-auto">
                        {formatDate(msg.createdAt)}
                      </span>
                    </div>
                    <p className="text-[13px] text-[var(--fg)] whitespace-pre-wrap break-words">
                      {msg.content}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Saved section
// ---------------------------------------------------------------------------

function SavedSection({ userId }: { userId: string }): React.JSX.Element {
  const [isLoading, setIsLoading] = React.useState(true);
  const [isError, setIsError] = React.useState(false);
  const [savedSources, setSavedSources] = React.useState<SavedSource[]>([]);
  const [userPlaces, setUserPlaces] = React.useState<UserPlace[]>([]);
  const getDataProvider = useDataProvider();
  const dpRef = React.useRef(getDataProvider());

  React.useEffect(() => {
    dpRef.current = getDataProvider();
  });

  const fetchSaved = React.useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const result = await dpRef.current.custom?.({
        url: `/admin/users/${userId}/saved`,
        method: "get",
      });
      const data = result?.data as SavedData | undefined;
      setSavedSources(data?.savedSources ?? []);
      setUserPlaces(data?.userPlaces ?? []);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  React.useEffect(() => {
    void fetchSaved();
  }, [fetchSaved]);

  if (isLoading) {
    return <SkeletonTable columns={3} rows={3} />;
  }

  if (isError) {
    return (
      <ErrorBanner
        message="Ошибка загрузки сохранённого"
        onRetry={() => { void fetchSaved(); }}
      />
    );
  }

  const isEmpty = savedSources.length === 0 && userPlaces.length === 0;
  if (isEmpty) {
    return <EmptyState message="Нет сохранённых материалов" />;
  }

  return (
    <div className="space-y-5">
      {savedSources.length > 0 && (
        <div>
          <h4 className="text-[12px] font-[600] uppercase tracking-wide text-[var(--muted)] mb-2">
            Источники ({savedSources.length})
          </h4>
          <div className="space-y-1.5">
            {savedSources.map((src) => (
              <div
                key={src.id}
                className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] px-4 py-2.5 flex gap-3 items-start"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-[500] text-[var(--fg)] truncate">
                    {src.title ?? src.url ?? src.id}
                  </div>
                  {src.type && (
                    <div className="text-[11px] text-[var(--muted)]">{src.type}</div>
                  )}
                </div>
                <span className="text-[11px] text-[var(--muted)] font-mono flex-shrink-0">
                  {formatDateShort(src.savedAt)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {userPlaces.length > 0 && (
        <div>
          <h4 className="text-[12px] font-[600] uppercase tracking-wide text-[var(--muted)] mb-2">
            Места ({userPlaces.length})
          </h4>
          <div className="space-y-1.5">
            {userPlaces.map((place) => (
              <div
                key={place.id}
                className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] px-4 py-2.5 flex gap-3 items-start"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-[500] text-[var(--fg)] truncate">
                    {place.name ?? place.id}
                  </div>
                  {place.address && (
                    <div className="text-[11px] text-[var(--muted)] truncate">{place.address}</div>
                  )}
                </div>
                <span className="text-[11px] text-[var(--muted)] font-mono flex-shrink-0">
                  {formatDateShort(place.savedAt)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export — top-level content tab with sub-sections
// ---------------------------------------------------------------------------

type ContentSection = "plans" | "chat" | "saved";

const SECTION_LABELS: Record<ContentSection, string> = {
  plans: "Планы",
  chat: "Чат",
  saved: "Сохранённое",
};

interface UserContentTabProps {
  userId: string;
}

export function UserContentTab({ userId }: UserContentTabProps): React.JSX.Element {
  const [activeSection, setActiveSection] = React.useState<ContentSection>("plans");

  const sections: ContentSection[] = ["plans", "chat", "saved"];

  return (
    <div>
      <div
        role="tablist"
        aria-label="Секции контента"
        className="flex gap-0 mb-5 border-b border-[var(--border)]"
      >
        {sections.map((section) => (
          <button
            key={section}
            type="button"
            role="tab"
            id={`content-tab-${section}`}
            aria-selected={activeSection === section}
            aria-controls={`content-panel-${section}`}
            className={[
              "px-4 h-9 text-[12px] font-[500] border-b-2 -mb-px transition-colors",
              activeSection === section
                ? "text-[var(--accent)] border-[var(--accent)] font-[600]"
                : "text-[var(--muted)] border-transparent hover:text-[var(--fg)]",
            ].join(" ")}
            onClick={() => setActiveSection(section)}
          >
            {SECTION_LABELS[section]}
          </button>
        ))}
      </div>

      <div
        id="content-panel-plans"
        role="tabpanel"
        aria-labelledby="content-tab-plans"
        hidden={activeSection !== "plans"}
      >
        {activeSection === "plans" && <PlansSection userId={userId} />}
      </div>

      <div
        id="content-panel-chat"
        role="tabpanel"
        aria-labelledby="content-tab-chat"
        hidden={activeSection !== "chat"}
      >
        {activeSection === "chat" && <ChatSection userId={userId} />}
      </div>

      <div
        id="content-panel-saved"
        role="tabpanel"
        aria-labelledby="content-tab-saved"
        hidden={activeSection !== "saved"}
      >
        {activeSection === "saved" && <SavedSection userId={userId} />}
      </div>
    </div>
  );
}
